import { createContext, useContext } from 'react';

export type DB = any;
export const DbContext = createContext<DB | undefined>(undefined);

export const useDb = () => {
  const db = useContext(DbContext);
  if (db === undefined) {
    throw new Error('useDb must be used within a DbProvider');
  }
  return db;
};

let db: DB = null;

export const initDb = async () => {
  if (db) return db;
  
  try {
    // @ts-ignore
    const SQL = await window.initSqlJs({
      locateFile: file => `https://cdnjs.cloudflare.com/ajax/libs/sql.js/1.8.0/${file}`
    });
    
    db = new SQL.Database();
    
    // Initialize schema with all required tables
    db.exec(`
      BEGIN TRANSACTION;

      CREATE TABLE IF NOT EXISTS facilities (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        name TEXT NOT NULL UNIQUE
      );

      CREATE TABLE IF NOT EXISTS position_titles (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        title TEXT NOT NULL UNIQUE
      );

      CREATE TABLE IF NOT EXISTS pay_rates (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        position_id INTEGER NOT NULL,
        rate DECIMAL(10,2) NOT NULL,
        FOREIGN KEY (position_id) REFERENCES position_titles(id) ON DELETE CASCADE
      );

      CREATE TABLE IF NOT EXISTS job_openings (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        facility_id INTEGER NOT NULL,
        position_id INTEGER NOT NULL,
        employment_type TEXT NOT NULL,
        pay_rate DECIMAL(10,2) NOT NULL,
        shift TEXT NOT NULL,
        start_time TEXT NOT NULL,
        end_time TEXT NOT NULL,
        description TEXT,
        status TEXT NOT NULL DEFAULT 'Open',
        candidate_name TEXT,
        notes TEXT,
        date_opened DATETIME DEFAULT CURRENT_TIMESTAMP,
        status_updated_at DATETIME,
        FOREIGN KEY (facility_id) REFERENCES facilities(id) ON DELETE CASCADE,
        FOREIGN KEY (position_id) REFERENCES position_titles(id) ON DELETE CASCADE
      );

      CREATE TABLE IF NOT EXISTS interviews (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        job_opening_id INTEGER NOT NULL,
        interview_date DATETIME NOT NULL,
        candidate_name TEXT NOT NULL,
        notes TEXT,
        FOREIGN KEY (job_opening_id) REFERENCES job_openings(id) ON DELETE CASCADE
      );

      -- Create indexes for better query performance
      CREATE INDEX IF NOT EXISTS idx_job_openings_status ON job_openings(status);
      CREATE INDEX IF NOT EXISTS idx_job_openings_facility ON job_openings(facility_id);
      CREATE INDEX IF NOT EXISTS idx_job_openings_position ON job_openings(position_id);
      CREATE INDEX IF NOT EXISTS idx_pay_rates_position ON pay_rates(position_id);
      CREATE INDEX IF NOT EXISTS idx_interviews_job ON interviews(job_opening_id);

      -- Insert initial data only if tables are empty
      INSERT OR IGNORE INTO facilities (id, name) VALUES 
        (1, 'Main Office Building'),
        (2, 'Tech Center'),
        (3, 'Medical Plaza'),
        (4, 'Corporate Tower');

      INSERT OR IGNORE INTO position_titles (id, title) VALUES
        (1, 'Janitor'),
        (2, 'Maintenance Technician'),
        (3, 'Building Manager'),
        (4, 'Security Guard');

      INSERT OR IGNORE INTO pay_rates (position_id, rate) VALUES
        (1, 15.00),
        (1, 17.50),
        (2, 20.00),
        (2, 22.50),
        (3, 25.00),
        (3, 27.50),
        (4, 18.00),
        (4, 20.00);

      -- Sample job openings
      INSERT OR IGNORE INTO job_openings (
        facility_id, 
        position_id, 
        employment_type,
        pay_rate,
        shift,
        start_time,
        end_time,
        description,
        status,
        date_opened
      ) VALUES 
        (1, 1, 'Full-time', 15.00, '1st', '07:00', '15:00', 'Day shift janitorial position', 'Open', CURRENT_TIMESTAMP),
        (2, 2, 'Full-time', 20.00, '2nd', '15:00', '23:00', 'Evening maintenance position', 'Open', CURRENT_TIMESTAMP),
        (3, 3, 'Full-time', 25.00, '1st', '09:00', '17:00', 'Building manager position', 'Open', CURRENT_TIMESTAMP),
        (4, 4, 'Part-time', 18.00, '3rd', '23:00', '07:00', 'Night security position', 'Open', CURRENT_TIMESTAMP);

      COMMIT;
    `);

    return db;
  } catch (error) {
    console.error('Failed to initialize database:', error);
    throw error;
  }
};